import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { name: "", photo_url: "", position: "", bio: "", education: "", experience: "", expertise: "", phone: "" };

export default function AdminPembimbing() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/pembimbing`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...g, expertise: (g.expertise || []).join(", ") }); setModalOpen(true); };
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    const payload = { ...form, expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await axios.put(`${API}/admin/pembimbing/${editing.id}`, payload);
      else await axios.post(`${API}/admin/pembimbing`, payload);
      toast.success("Pembimbing berhasil disimpan");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/pembimbing/${deleteId}`);
      toast.success("Pembimbing berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div data-testid="admin-pembimbing-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Pembimbing</h1>
          <p className="text-sm text-[#64748B]">Kelola profil pembimbing Haji dan Umrah.</p>
        </div>
        <Button data-testid="admin-pembimbing-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Pembimbing</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={UserCog} title="Belum ada pembimbing" description="Tambahkan profil pembimbing pertama Anda." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((g) => (
            <div key={g.id} data-testid={`admin-pembimbing-card-${g.id}`} className="rounded-xl border border-slate-100 bg-white p-5">
              <div className="flex items-center gap-3">
                <img src={g.photo_url} alt={g.name} className="h-12 w-12 rounded-full object-cover" />
                <div>
                  <p className="font-medium text-[#1F2937]">{g.name}</p>
                  <p className="text-xs text-[#64748B]">{g.position}</p>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="ghost" size="icon" data-testid={`admin-pembimbing-edit-${g.id}`} onClick={() => openEdit(g)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`admin-pembimbing-delete-${g.id}`} onClick={() => setDeleteId(g.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="admin-pembimbing-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Pembimbing" : "Tambah Pembimbing"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input data-testid="guide-form-name" value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
            <div><Label>URL Foto</Label><Input data-testid="guide-form-photo" value={form.photo_url} onChange={(e) => update("photo_url", e.target.value)} /></div>
            <div><Label>Jabatan</Label><Input data-testid="guide-form-position" value={form.position} onChange={(e) => update("position", e.target.value)} /></div>
            <div><Label>Biografi</Label><Textarea data-testid="guide-form-bio" value={form.bio} onChange={(e) => update("bio", e.target.value)} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pendidikan</Label><Input data-testid="guide-form-education" value={form.education} onChange={(e) => update("education", e.target.value)} /></div>
              <div><Label>Pengalaman</Label><Input data-testid="guide-form-experience" value={form.experience} onChange={(e) => update("experience", e.target.value)} /></div>
            </div>
            <div><Label>Keahlian (pisahkan dengan koma)</Label><Input data-testid="guide-form-expertise" value={form.expertise} onChange={(e) => update("expertise", e.target.value)} /></div>
            <div><Label>Nomor HP</Label><Input data-testid="guide-form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></div>
            <Button data-testid="admin-pembimbing-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Pembimbing?" onConfirm={handleDelete} testId="admin-pembimbing-delete-confirm" />
    </div>
  );
}
